/* GEOTECH · Product grid (ARC)
   Tabs, sort, quick add, notify dialog, drop launch timer, sticky buy bar. */
(() => {
  if (customElements.get('geotech-product-grid')) return;

  const addUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  class GeotechProductGrid extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.list = this.querySelector('[data-gpg-list]');
      this.items = Array.from(this.querySelectorAll('[data-gpg-item]'));
      this.tiles = Array.from(this.querySelectorAll('[data-gpg-tile]'));
      this.tabs = Array.from(this.querySelectorAll('[data-gpg-tab]'));
      this.sortSelect = this.querySelector('[data-gpg-sort]');
      this.countEls = Array.from(this.querySelectorAll('[data-gpg-count]'));
      this.emptyEl = this.querySelector('[data-gpg-empty]');
      this.dialog = this.querySelector('[data-gpg-dialog]');
      this.sticky = this.querySelector('[data-gpg-sticky]');
      this.pendingControllers = new Set();

      this.items.forEach((item, index) => {
        item.dataset.index = String(index);
      });

      this.activeTab =
        this.tabs.find((tab) => tab.getAttribute('aria-pressed') === 'true') || this.tabs[0] || null;

      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab)));
      if (this.sortSelect) {
        this.sortSelect.value = 'featured';
        this.fitSelect();
        this.sortSelect.addEventListener('change', () => {
          this.fitSelect();
          this.apply();
        });
      }

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.querySelector('[data-gpg-picker]:not([hidden])')) {
          event.stopPropagation();
          this.closePickers(true);
        }
      });

      this.onDocumentClick = (event) => {
        if (event.target.closest('[data-gpg-picker], [data-gpg-picker-toggle]')) return;
        this.closePickers();
      };
      document.addEventListener('click', this.onDocumentClick);

      this.onBlockSelect = (event) => {
        const tab = this.tabs.find((t) => t.contains(event.target) || t === event.target);
        if (tab) this.selectTab(tab);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.onPageShow = () => this.resetNativeForms();
      window.addEventListener('pageshow', this.onPageShow);

      if (this.activeTab && this.activeTab.dataset.filter !== 'all') this.apply();

      this.checkDrops();
      this.initSticky();
      this.initDialog();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.dropTimer);
      clearTimeout(this.nativeSubmitTimer);
      this.pendingControllers.forEach((controller) => controller.abort());
      this.pendingControllers.clear();
    }

    /* ---------- Tabs + sort ---------- */

    selectTab(tab) {
      if (tab === this.activeTab) return;
      this.tabs.forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
      this.activeTab = tab;

      const strip = tab.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const left = tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
      }

      this.apply();
    }

    fitSelect() {
      const select = this.sortSelect;
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const probe = document.createElement('span');
      const style = getComputedStyle(select);
      probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${style.font};letter-spacing:${style.letterSpacing}`;
      probe.textContent = option.textContent;
      document.body.appendChild(probe);
      select.style.width = `${Math.ceil(probe.getBoundingClientRect().width) + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + 2}px`;
      probe.remove();
    }

    matches(item) {
      const tab = this.activeTab;
      if (!tab || tab.dataset.filter === 'all') return true;
      const { collection, tag, type } = tab.dataset;
      return Boolean(
        (collection && (item.dataset.collections || '').includes(`,${collection},`)) ||
          (tag && (item.dataset.tags || '').includes(`|${tag}|`)) ||
          (type && item.dataset.type === type)
      );
    }

    compare(a, b, mode) {
      const num = (el, key) => Number(el.dataset[key]) || 0;

      if (mode !== 'featured') {
        const archived = (el) => (el.dataset.status === 'archive' ? 1 : 0);
        const diff = archived(a) - archived(b);
        if (diff) return diff;
      }

      let diff = 0;
      switch (mode) {
        case 'price-asc':
          diff = num(a, 'price') - num(b, 'price');
          break;
        case 'price-desc':
          diff = num(b, 'price') - num(a, 'price');
          break;
        case 'reviews':
          diff = num(b, 'reviews') - num(a, 'reviews');
          break;
        case 'newest':
          diff = num(b, 'published') - num(a, 'published');
          break;
        default:
          diff = 0;
      }
      return diff || num(a, 'index') - num(b, 'index');
    }

    apply() {
      if (!this.list) return;

      const mode = this.sortSelect ? this.sortSelect.value : 'featured';
      const isAll = !this.activeTab || this.activeTab.dataset.filter === 'all';
      const showTiles = isAll && mode === 'featured';
      const sorted = [...this.items].sort((a, b) => this.compare(a, b, mode));

      const tilesByPosition = new Map();
      this.tiles.forEach((tile) => {
        const position = Number(tile.dataset.position) || 0;
        if (!tilesByPosition.has(position)) tilesByPosition.set(position, []);
        tilesByPosition.get(position).push(tile);
        tile.hidden = !showTiles;
      });

      const fragment = document.createDocumentFragment();
      let visible = 0;

      sorted.forEach((item) => {
        const match = this.matches(item);
        item.hidden = !match;
        fragment.appendChild(item);
        if (!match) return;

        visible += 1;
        if (showTiles && tilesByPosition.has(visible)) {
          tilesByPosition.get(visible).forEach((tile) => fragment.appendChild(tile));
          tilesByPosition.delete(visible);
        }
      });

      tilesByPosition.forEach((tiles) => tiles.forEach((tile) => fragment.appendChild(tile)));

      this.closePickers();
      this.list.appendChild(fragment);
      this.countEls.forEach((el) => {
        el.textContent = String(visible);
      });
      if (this.emptyEl) this.emptyEl.hidden = visible > 0;
    }

    /* ---------- Clicks ---------- */

    onClick(event) {
      const toggle = event.target.closest('[data-gpg-picker-toggle]');
      if (toggle) {
        event.preventDefault();
        this.togglePicker(toggle);
        return;
      }

      if (event.target.closest('[data-gpg-picker-close]')) {
        this.closePickers(true);
        return;
      }

      const chip = event.target.closest('[data-gpg-chip]');
      if (chip) {
        this.chooseVariant(chip);
        return;
      }

      const notify = event.target.closest('[data-gpg-notify]');
      if (notify) {
        event.preventDefault();
        this.openDialog(notify);
        return;
      }

      if (event.target.closest('[data-gpg-dialog-close]') && this.dialog) {
        this.dialog.close();
      }
    }

    /* ---------- Quick add ---------- */

    togglePicker(toggle) {
      const picker = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!picker) return;
      const shouldOpen = picker.hidden;
      this.closePickers();
      if (!shouldOpen) return;

      picker.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      const firstChip = picker.querySelector('[data-gpg-chip]:not(:disabled)');
      if (firstChip) firstChip.focus({ preventScroll: true });
    }

    closePickers(returnFocus = false) {
      this.querySelectorAll('[data-gpg-picker]:not([hidden])').forEach((picker) => {
        picker.hidden = true;
        const toggle = this.querySelector(`[aria-controls="${picker.id}"]`);
        if (!toggle) return;
        toggle.setAttribute('aria-expanded', 'false');
        if (returnFocus) toggle.focus({ preventScroll: true });
      });
    }

    chooseVariant(chip) {
      const scope = chip.closest('[data-gpg-add-scope]');
      const form = scope && scope.querySelector('form[data-gpg-add]');
      if (!form) return;
      form.querySelector('[name="id"]').value = chip.dataset.variantId;
      this.addToCart(form, chip);
    }

    onSubmit(event) {
      const notifyForm = event.target.closest('form[data-gpg-notify-form]');
      if (notifyForm) {
        this.prepareNativeSubmit(event, notifyForm);
        return;
      }

      const form = event.target.closest('form[data-gpg-add]');
      if (!form) return;
      event.preventDefault();
      this.addToCart(form, event.submitter || form.querySelector('[type="submit"]'));
    }

    async addToCart(form, trigger) {
      const scope = form.closest('[data-gpg-add-scope]') || form;
      const button = scope.querySelector('.gpg-btn--add') || trigger;
      if (!button || button.getAttribute('aria-disabled') === 'true') return;

      const busy = [...new Set([trigger, button].filter(Boolean))];
      const message = scope.querySelector('[data-gpg-status]');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = new FormData(form);

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
      }

      busy.forEach((el) => {
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('aria-busy', 'true');
        if ('disabled' in el) el.disabled = true;
        el.classList.add('is-loading');
      });
      this.setMessage(message, 'Adding to bag…', 0, 'loading');

      const controller = new AbortController();
      this.pendingControllers.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(addUrl(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
          signal: controller.signal,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'This item could not be added.');
        }

        this.closePickers();

        if (!cart || typeof cart.renderContents !== 'function') {
          window.location.href = cartUrl();
          return;
        }

        cart.classList.remove('is-empty');
        cart.renderContents(data);
        this.flash(button, 'is-added', 900);
        this.setMessage(message, 'Added to bag.', 1600, 'success');

        // Start the visible drawer update first; non-visual subscribers can react on the next frame.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          requestAnimationFrame(() => {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'geotech-product-grid',
              productVariantId: body.get('id'),
              cartData: data,
            });
          });
        }
      } catch (error) {
        if (!this.isConnected && error?.name === 'AbortError') return;
        const detail = error?.name === 'AbortError'
          ? 'Adding took too long. Please try again.'
          : (error?.message || 'This item could not be added. Please try again.');
        this.setMessage(message, detail, 0, 'error');
      } finally {
        window.clearTimeout(timeout);
        this.pendingControllers.delete(controller);
        busy.forEach((el) => {
          el.removeAttribute('aria-disabled');
          el.removeAttribute('aria-busy');
          if ('disabled' in el) el.disabled = false;
          el.classList.remove('is-loading');
        });
      }
    }

    flash(el, className, duration = 1800) {
      el.classList.add(className);
      clearTimeout(el.gpgFlashTimer);
      el.gpgFlashTimer = setTimeout(() => el.classList.remove(className), duration);
    }

    setMessage(el, text, clearAfter = 0, state = '') {
      if (!el) return;
      el.textContent = text;
      if (state) el.dataset.state = state;
      else delete el.dataset.state;
      clearTimeout(el.gpgMessageTimer);
      if (text && clearAfter) {
        el.gpgMessageTimer = setTimeout(() => {
          el.textContent = '';
          delete el.dataset.state;
        }, clearAfter);
      }
    }

    prepareNativeSubmit(event, form) {
      if (form.dataset.gpgSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      const button = event.submitter || form.querySelector('[type="submit"]');
      form.dataset.gpgSubmitting = 'true';
      form.setAttribute('aria-busy', 'true');

      if (button) {
        button.dataset.gpgOriginalLabel = button.textContent.trim();
        button.style.minWidth = `${Math.ceil(button.getBoundingClientRect().width)}px`;
        button.textContent = 'Sending…';
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      }

      clearTimeout(this.nativeSubmitTimer);
      this.nativeSubmitTimer = window.setTimeout(() => this.resetNativeForms(), 15000);
    }

    resetNativeForms() {
      clearTimeout(this.nativeSubmitTimer);
      this.querySelectorAll('form[data-gpg-notify-form][data-gpg-submitting="true"]').forEach((form) => {
        delete form.dataset.gpgSubmitting;
        form.removeAttribute('aria-busy');
        const button = form.querySelector('[type="submit"]');
        if (!button) return;
        if (button.dataset.gpgOriginalLabel) button.textContent = button.dataset.gpgOriginalLabel;
        delete button.dataset.gpgOriginalLabel;
        button.style.removeProperty('min-width');
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      });
    }

    /* ---------- Drops ---------- */

    checkDrops() {
      clearTimeout(this.dropTimer);
      const now = Date.now() / 1000;
      let next = Infinity;

      this.querySelectorAll('[data-drop-ts]').forEach((el) => {
        if (el.dataset.launched) return;
        const ts = Number(el.dataset.dropTs);
        if (!ts) return;
        if (ts <= now) {
          this.launch(el);
        } else {
          next = Math.min(next, ts);
        }
      });

      const wait = (next - now) * 1000;
      if (Number.isFinite(wait) && wait < 2147483000) {
        this.dropTimer = setTimeout(() => this.checkDrops(), wait + 250);
      }
    }

    launch(el) {
      el.dataset.launched = 'true';
      const status = el.dataset.available === 'true' ? 'available' : 'soldout';
      el.dataset.status = status;

      const card = el.querySelector('.gpg-card');
      if (card) card.classList.replace('gpg-card--drop', `gpg-card--${status}`);

      el.querySelectorAll('[data-gpg-prelaunch]').forEach((node) => {
        node.hidden = true;
      });
      el.querySelectorAll('[data-gpg-postlaunch]').forEach((node) => {
        node.hidden = false;
      });
    }

    /* ---------- Sticky bar ---------- */

    initSticky() {
      if (!this.sticky || !this.list) return;
      this.setStickyVisible(false);
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.setStickyVisible(entry.isIntersecting);
        },
        { rootMargin: '0px 0px -25% 0px' }
      );
      this.observer.observe(this.list);
    }

    setStickyVisible(visible) {
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.toggleAttribute('inert', !visible);
      if (!visible) {
        this.sticky.querySelectorAll('[data-gpg-picker]:not([hidden])').forEach((picker) => {
          picker.hidden = true;
        });
      }
    }

    /* ---------- Notify dialog ---------- */

    initDialog() {
      const dialog = this.dialog;
      if (!dialog || typeof dialog.showModal !== 'function') return;

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      if (dialog.querySelector('[data-gpg-form-state]')) dialog.showModal();
    }

    openDialog(trigger) {
      const detail = {
        kind: trigger.dataset.kind || 'restock',
        title: trigger.dataset.productTitle || '',
        handle: trigger.dataset.productHandle || '',
        url: trigger.dataset.productUrl || '',
        variantId: trigger.dataset.variantId || '',
      };

      // Apps (Klaviyo, Back in Stock, etc.) can call event.preventDefault() to use their own form.
      const proceed = this.dispatchEvent(
        new CustomEvent('geotech:notify', { bubbles: true, cancelable: true, detail })
      );
      if (!proceed) return;

      const dialog = this.dialog;
      if (!dialog || typeof dialog.showModal !== 'function') {
        if (detail.url) window.location.href = detail.url;
        return;
      }

      const eyebrow = dialog.querySelector('[data-gpg-dialog-eyebrow]');
      const title = dialog.querySelector('[data-gpg-dialog-title]');
      const tags = dialog.querySelector('[data-gpg-dialog-tags]');

      if (eyebrow) eyebrow.textContent = eyebrow.dataset[detail.kind] || eyebrow.dataset.restock;
      if (title) title.textContent = detail.title || title.dataset.default;
      if (tags) {
        const base = tags.dataset.base || '';
        const prefix = detail.kind === 'drop' ? 'drop' : 'restock';
        tags.value = detail.handle ? [base, `${prefix}-${detail.handle}`].filter(Boolean).join(', ') : base;
      }

      dialog.querySelectorAll('[data-gpg-form-state]').forEach((node) => node.remove());
      dialog.showModal();
      const input = dialog.querySelector('input[type="email"]');
      if (input) input.focus();
    }
  }

  customElements.define('geotech-product-grid', GeotechProductGrid);
})();
